import { useEffect, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Timer } from "../ui/Timer.js";
import { useStore } from "./state.js";
import { shopsApi } from "./api.js";
import { OfferCard } from "./cards.js";
import type { StoreCard } from "./types.js";

/** Token shops: arena, prestige, salvage, event and black market with period limits (R57). */
/** `only` shows a single shop without the shop tabs (the alliance hub embeds its shop, D72). */
export function ShopsView({ only }: { only?: string } = {}) {
  const t = useT();
  const shops = useStore((s) => s.shops);
  const refreshShops = useStore((s) => s.refreshShops);
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const [shopId, setShopId] = useState<string | null>(null);
  useEffect(() => {
    void refreshShops();
  }, [refreshShops]);
  if (!shops) return <p className="hint">{t("common.loading")}</p>;
  const visible = only ? shops.filter((s) => s.id === only) : shops;
  const shop = visible.find((s) => s.id === shopId) ?? visible[0];
  if (!shop) return null;
  const buy = async (card: StoreCard) => {
    const res = await run(() => shopsApi.buy(shop.id, card.key), t("store.shop.bought"));
    if (res) {
      celebrate({ key: card.key, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      void refreshShops();
    }
  };
  return (
    <Panel title={t("store.tab.shops")}>
      <div className="row tabs" hidden={visible.length < 2}>
        {visible.map((s) => (
          <button key={s.id} type="button" className={`tab ${s.id === shop.id ? "active" : ""}`} onClick={() => setShopId(s.id)} data-testid={`shop-tab-${s.id}`}>
            {pick(s.name)}
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="badge mono" data-testid="shop-balance">
          {shop.balance} {shop.currency}
        </span>
        <span className="dim mono" style={{ fontSize: 11 }}>
          {t(`store.shop.refresh.${shop.refresh}`)} · <Timer finishesAt={shop.resetsAt} />
        </span>
      </div>
      <div className="offers" data-testid={`shop-${shop.id}`}>
        {shop.items.map((c) => (
          <OfferCard key={c.key} card={c} onBuy={(card) => void buy(card)} testId={`shop-item-${c.key}`} />
        ))}
      </div>
    </Panel>
  );
}
